import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/UserManagement.css';
import usuariosApiService from '../services/usuariosApiService';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Cargar usuarios desde la API
  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await usuariosApiService.getAllUsuarios();
      console.log('Usuarios obtenidos:', data);
      
      // Transformar los datos del backend al formato esperado por el frontend
      const usuariosTransformados = data.map(usuario => ({
        id: usuario.usuarioId || usuario.USUARIO_ID,
        fullName: usuario.nombreCompleto || usuario.NOMBRE_COMPLETO || 'Sin nombre',
        username: usuario.username || usuario.USUARIO_USER || 'N/A',
        status: usuario.estado || usuario.USUARIO_ESTADO || 'Inactivo',
        permissions: Array.isArray(usuario.permisos) ? usuario.permisos : [],
        permissionsIds: Array.isArray(usuario.permisosIds) ? usuario.permisosIds : []
      }));
      
      setUsers(usuariosTransformados);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuarios según el término de búsqueda
  const filteredUsers = users.filter(user =>
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginatedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const handleRegisterUser = () => {
    navigate('/register-user');
  };

  const handleEditUser = (userId) => {
    navigate(`/edit-user/${userId}`);
  };

  const handleResetPassword = async (userId) => {
    try {
      const confirmReset = window.confirm('¿Está seguro de resetear la contraseña a la predeterminada (fecha de nacimiento)?');
      if (!confirmReset) return;
      
      await usuariosApiService.resetearContrasenia(userId);
      alert('La contraseña ha sido restablecida exitosamente');
    } catch (error) {
      console.error('Error al resetear contraseña:', error);
      alert('Error al restablecer la contraseña');
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const usr = users.find(u => u.id === userId);
      if (!usr) return;
      
      const next = usr.status === 'Activo' ? 'Inactivo' : 'Activo';
      const confirmChange = window.confirm(`¿Está seguro de ${next === 'Activo' ? 'activar' : 'desactivar'} este usuario?`);
      if (!confirmChange) return;
      
      // Obtener los IDs de permisos actuales del usuario
      const permisosIds = usr.permissionsIds || [];
      
      await usuariosApiService.actualizarUsuario(userId, next, permisosIds);
      
      // Recargar la lista completa
      await loadUsuarios();
      alert(`Usuario ${next === 'Activo' ? 'activado' : 'desactivado'} exitosamente`);
    } catch (error) {
      console.error('Error al cambiar estado del usuario:', error);
      alert('Error al cambiar el estado del usuario');
    }
  };

  return (
    <div className="user-management-container">
      <div className="page-header">
        <div className="header-title">
          <h1>Gestión de Usuarios</h1>
          <p>Administra las cuentas de usuario del sistema</p>
        </div>
      </div>

      <div className="user-management-actions">
        <div className="search-section">
          <div className="search-input">
            <input
              type="text"
              placeholder="Buscar por nombre o usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="button" title="Buscar">
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
        
        <button 
          className="btn-primary register-btn create-btn"
          onClick={handleRegisterUser}
        >
          <i className="fas fa-user-plus"></i>
          Registrar Usuario
        </button>
      </div>

      <div className="users-table-container">
        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Cargando usuarios...</p>
          </div>
        ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre Completo</th>
              <th>Usuario</th>
              <th>Estado</th>
              <th>Permisos</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map(user => (
              <tr key={user.id}>
                <td><span className="code-id-field">{user.id}</span></td>
                <td className="user-name">
                  <div className="user-avatar">
                    <i className="fas fa-user"></i>
                  </div>
                  <span>{user.fullName}</span>
                </td>
                <td className="username">{user.username}</td>
                <td>
                  <span className={`status-badge ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>
                  <div className="permissions">
                    {user.permissions.map((permission, index) => (
                      <span key={index} className="permission-badge">
                        {permission}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="actions-cell">
                  <button 
                    className="action-btn edit-btn"
                    onClick={() => handleEditUser(user.id)}
                    title="Editar Usuario"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="action-btn reset-btn"
                    onClick={() => handleResetPassword(user.id)}
                    title="Restablecer Contraseña"
                  >
                    <i className="fas fa-key"></i>
                  </button>
                  <button 
                    className={`action-btn ${user.status === 'Activo' ? 'deactivate-btn' : 'activate-btn'}`}
                    onClick={() => handleDeactivateUser(user.id)}
                    title={user.status === 'Activo' ? 'Desactivar Usuario' : 'Activar Usuario'}
                  >
                    <i className={`fas ${user.status === 'Activo' ? 'fa-user-times' : 'fa-user-check'}`}></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {!loading && (
        <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            Mostrando <strong>{paginatedUsers.length}</strong> de <strong>{total}</strong> usuarios
          </div>
          <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
            <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
            <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
            <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Por página:</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
